import { useQuery } from '@apollo/react-hooks';

import {
	type FeatureId,
	type InTrialFeatureGatesDataSource,
	type ITFGAnalyticsCallbackParams,
} from '../../common/types';

import { InTrialFeatureGatesBannerQuery } from './constants';
import { type InTrialFeatureGateBannerData } from './types';
import { addOriginToPaymentFlowUrl, getTrialEndDate } from './utils';

type UseGetInTrialFeatureGateBannerDataProps = {
	featureId: FeatureId;
	sendOperationalEvent: (params: ITFGAnalyticsCallbackParams) => void;
	skip?: boolean;
	tenantId: string;
} & InTrialFeatureGatesDataSource;

export const useGetInTrialFeatureGateBannerData = ({
	// Apps have different ways of getting the AGG client. Its not always as simple as useClient() and depends on the app's architecture.
	// Therefore, we need to pass it in as a prop.
	tenantId,
	sendOperationalEvent,
	featureId,
	skip = false,
	...props
}: UseGetInTrialFeatureGateBannerDataProps): InTrialFeatureGateBannerData => {
	const aggClient = 'aggClient' in props ? props.aggClient : undefined;
	const { inTrialFeatureGatesQueryResult } =
		'inTrialFeatureGatesQueryResult' in props
			? props
			: { inTrialFeatureGatesQueryResult: undefined };

	const queryResult = useQuery(InTrialFeatureGatesBannerQuery, {
		client: aggClient,
		variables: {
			cloudId: tenantId,
		},
		skip: skip || !!inTrialFeatureGatesQueryResult,
	});

	const { data, error, loading } = inTrialFeatureGatesQueryResult ?? queryResult;

	if (error) {
		sendOperationalEvent({
			type: 'sendOperationalEvent',
			action: 'error',
			actionSubject: 'luna-growth-experiment-graphql-error',
			actionSubjectId: `in-trial-feature-gates-banner-graphql-error`,
			attributes: {
				error: error?.message,
				extraInfo: 'extraInfo' in error ? error.extraInfo : undefined,
				graphqlErrors: 'graphQLErrors' in error ? error.graphQLErrors : undefined,
				networkErrors: 'networkError' in error ? error.networkError : undefined,
				tenantId,
				featureId,
			},
			source: 'in_trial_feature_gates_banner',
		});

		return {
			error,
			loading: !!loading,
			paymentFlowExperienceUrl: undefined,
			hasPaymentDetailsOnFile: false,
			canUserPayAndUpgrade: false,
			daysLeftInTrial: undefined,
		};
	}

	const invoiceGroup =
		data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.subscription?.accountDetails
			?.invoiceGroup;
	const subscription = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.subscription;
	const experienceCapabilities = invoiceGroup?.experienceCapabilities;

	/**
	 * Calculate core data for the in-trial feature gate banner
	 */
	const paymentFlowExperienceUrl = addOriginToPaymentFlowUrl({
		paymentExperienceUrlV1: experienceCapabilities?.configurePayment?.experienceUrl,
		paymentExperienceUrlV2: experienceCapabilities?.configurePaymentV2?.experienceUrl,
	});
	const hasPaymentDetailsOnFile = !!subscription?.accountDetails?.invoiceGroup?.invoiceable;
	const canUserPayAndUpgrade =
		!!experienceCapabilities?.configurePaymentV2?.isAvailableToUser ||
		!!experienceCapabilities?.configurePayment?.isAvailableToUser;
	const daysLeftInTrial = getTrialEndDate(subscription?.trial?.endTimestamp);

	return {
		error,
		loading: !!loading,
		paymentFlowExperienceUrl,
		hasPaymentDetailsOnFile,
		canUserPayAndUpgrade,
		daysLeftInTrial,
	};
};
